import { getConfig } from '@app/config';
import { ItemList, Keyword, KeywordSet, Offer } from '@store/domain';
import { format } from 'date-fns';
import { Writable } from 'svelte/store';

export type KeyStringValuePair = { [key: string]: string };
export type KeyBooleanValuePair = { [key: string]: boolean };

export interface FilterParam {
  audience?: string[]; // Keyword IDs (audience)
  keywords?: string[]; // Keyword IDs
  area?: string; // Area IDs
  searchQuery?: string;
  startDate?: Date;
}

/**
 *
 * @param queryParams Query params as a key/value (string) pair
 * @returns A string of query params joined with &-sign
 */
export function createQueryParamString(queryParams: KeyStringValuePair): string {
  return Object.keys(queryParams)
    .map(paramName => `${paramName}=${queryParams[paramName]}`)
    .join('&');
}

/**
 * @returns an ItemList with empty values
 */
export function initEmptyItemList<T>(): ItemList<T> {
  return {
    meta: { count: 0, next: null, previous: null },
    data: []
  };
}

/**
 *
 * @param start_time From API (e.g. 2020-01-01T12:30:00.000Z)
 * @param end_time  From API (e.g. 2020-01-01T12:30:00.000Z)
 * @param displayTime
 * @param displayOnlyTime note that if this is true, it overrides displayTime
 */
export function resolveDatePhrase(start_time: string, end_time: string | null, displayTime?: boolean, displayOnlyTime?: boolean): string {
  const { dateFormat, timeFormat } = getConfig().site;

  const startAsDate = new Date(start_time);
  const endAsDate = end_time ? new Date(end_time) : null;

  const startDateFormatted = format(startAsDate, dateFormat);
  const endDateFormatted = endAsDate ? format(endAsDate, dateFormat) : null;

  const startTimeFormatted = format(startAsDate, timeFormat);
  const endTimeFormatted = endAsDate ? format(endAsDate, timeFormat) : null;

  var datePhrase = '';

  if (!displayOnlyTime && startDateFormatted === endDateFormatted) {
    datePhrase = startDateFormatted;
  } else if (!displayOnlyTime) {
    if (endDateFormatted) {
      datePhrase = `${startDateFormatted}-${endDateFormatted}`;
    } else {
      datePhrase = `${startDateFormatted}`;
    }
  } else {
  }

  if (displayTime && !displayOnlyTime) {
    if (startTimeFormatted === endTimeFormatted) {
      datePhrase = `${datePhrase} ${startTimeFormatted}`;
    } else {
      if (endTimeFormatted) {
        datePhrase = `${datePhrase} ${startTimeFormatted}-${endTimeFormatted}`;
      } else {
        datePhrase = `${datePhrase} ${startTimeFormatted}`;
      }
    }
  } else if (displayOnlyTime) {
    if (startTimeFormatted === endTimeFormatted) {
      datePhrase = startTimeFormatted;
    } else {
      if (endTimeFormatted) {
        datePhrase = `${startTimeFormatted}-${endTimeFormatted}`;
      } else {
        datePhrase = `${startTimeFormatted}`;
      }
    }
  }

  return datePhrase;
}

export function resolveOffers(offers: Offer[]) {
  return {
    is_free: offers.some(o => o.is_free)
  };
}

/**
 *
 * @param keywordSetList
 * @param usage This can be 'keyword' or 'audience'
 */
export function resolveAudienceList(keywordSetList: Array<KeywordSet>, usage: string) {
  return keywordSetList
    .filter(a => a.usage === usage)
    .reduce((keywords: Array<Keyword>, keywordSet: KeywordSet) => {
      const tmp = keywords.concat(keywordSet.keywords);
      return tmp;
    }, []);
}

/**
 * Return true if value is undefined or has the length of 0 when trimmed of whitespace
 * @param value
 */
export function isEmptyOrUndefinedString(value: string): boolean {
  return value != null && typeof value === 'string' ? value.trim().length === 0 : true;
}

/**
 * Return calculated integer of pages based on page-size and number of items
 * @param itemCount Total number of items
 * @param pageSize Items shown per page
 */
export function getPageCount(itemCount: number, pageSize: number): number {
  return itemCount > 0 ? ((itemCount + pageSize - 1) / pageSize) | 0 : 1;
}
